import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = (typeof window !== 'undefined' && window.__MIU_ACTIVE_BACKEND_URL__) || process.env.REACT_APP_BACKEND_URL;

/**
 * AuthCallback - Handles Google OAuth callback
 * Processes session_id from URL hash and exchanges it for user data
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processGoogleAuth = async () => {
      // Extract all params from URL hash fragment
      const hash = location.hash.substring(1); // Remove the # 
      const params = new URLSearchParams(hash);
      
      const sessionId = params.get('session_id');
      
      // Debug logging
      
      if (!sessionId) {
        console.error('No session_id found in URL hash');
        navigate('/login', { replace: true, state: { error: 'Authentication failed - no session' } });
        return;
      }

      try {
        // Exchange session_id for user data via our backend
        const response = await axios.post(
          `${API}/api/auth/google/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        const { token, refresh_token, user, is_new_user } = response.data;

        // Store token and user in auth context (with refresh token)
        loginWithToken(token, user, refresh_token);

        // Always go to dashboard — Browse owns the profile-completion gate now.
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Google auth error:', error.response?.data || error.message);
        navigate('/login', { 
          replace: true, 
          state: { error: error.response?.data?.detail || 'Google authentication failed. Please try again.' } 
        });
      }
    };

    processGoogleAuth();
  }, [location.hash, navigate, loginWithToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-slate-50">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#E8430A] mx-auto mb-4" />
        <p className="text-slate-600 font-medium">Completing sign in...</p>
        <p className="text-slate-400 text-sm mt-2">Please wait...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
